import { useEffect, useState } from "react"
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts"
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded"
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded"
import CoffeeIcon from "@mui/icons-material/Coffee"
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import "./chart.css"

const TABS = [
  { key: "hotels", label: "Hôtels",    color: "#6366f1", icon: ApartmentRoundedIcon,    endpoint: "https://api.moroccool.com/api/hotels" },
  { key: "cafes",  label: "Cafés",     color: "#10b981", icon: CoffeeIcon,              endpoint: "https://api.moroccool.com/api/cafes"  },
  { key: "events", label: "Événements",color: "#f59e0b", icon: ConfirmationNumberIcon,  endpoint: "https://api.moroccool.com/api/events" },
]

const TIME_FILTERS = ["day", "week", "month", "year"]

const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"]
const DAYS   = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"]
const WEEKS  = ["Sem 1","Sem 2","Sem 3","Sem 4","Sem 5"]

const PIE_COLORS = {
  hotels: ["#6366f1","#8b5cf6","#a78bfa","#c4b5fd"],
  cafes:  ["#10b981","#059669","#14b8a6","#34d399"],
  events: ["#f59e0b","#d97706","#fb923c","#fcd34d"],
}

const generateTrend = (items, key, timeFilter) => {
  const grouped = {}
  items.forEach(item => {
    const date = new Date(item.createdAt || item.date || item.updatedAt || Date.now())
    let bucket
    if (timeFilter === "day")   bucket = DAYS[date.getDay() === 0 ? 6 : date.getDay() - 1]
    if (timeFilter === "week")  bucket = `Sem ${Math.ceil(date.getDate() / 7)}`
    if (timeFilter === "month") bucket = MONTHS[date.getMonth()]
    if (timeFilter === "year")  bucket = String(date.getFullYear())
    grouped[bucket] = (grouped[bucket] || 0) + 1
  })

  let keys
  if (timeFilter === "day")   keys = DAYS
  if (timeFilter === "week")  keys = WEEKS
  if (timeFilter === "month") keys = MONTHS
  if (timeFilter === "year")  keys = [...new Set(Object.keys(grouped))].sort()

  return keys.map(k => ({ period: k, [key]: grouped[k] || 0 }))
}

const generatePie = (items) => {
  const grouped = {}
  items.forEach(item => {
    const t = item.type || item.category || item.classification || "Autre"
    grouped[t] = (grouped[t] || 0) + 1
  })
  return Object.entries(grouped)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([name, value]) => ({
      name,
      value,
      percent: items.length ? ((value / items.length) * 100).toFixed(1) : "0",
    }))
}

const CustomTooltip = ({ active, payload, label, color }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="chart-tooltip">
      <p className="tt-label">{label}</p>
      <p className="tt-value" style={{ color }}>{payload[0].value}</p>
    </div>
  )
}

const Chart = ({ aspect = 3 }) => {
  const [activeTab, setActiveTab]   = useState("hotels")
  const [timeFilter, setTimeFilter] = useState("month")
  const [data, setData]             = useState({})
  const [loading, setLoading]       = useState(true)
  const [transitioning, setTransitioning] = useState(false)

  const tab = TABS.find(t => t.key === activeTab)

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true)
      const results = {}
      await Promise.all(
        TABS.map(async t => {
          try {
            const res = await fetch(t.endpoint)
            const items = await res.json()
            results[t.key] = {
              total:    Array.isArray(items) ? items.length : 0,
              trend:    generateTrend(Array.isArray(items) ? items : [], t.key, timeFilter),
              pie:      generatePie(Array.isArray(items) ? items : []),
              rawItems: Array.isArray(items) ? items : [],
            }
          } catch {
            results[t.key] = { total: 0, trend: [], pie: [], rawItems: [] }
          }
        })
      )
      setData(results)
      setLoading(false)
    }
    fetchAll()
  }, [timeFilter])

  const switchTab = (key) => {
    if (key === activeTab) return
    setTransitioning(true)
    setTimeout(() => { setActiveTab(key); setTransitioning(false) }, 180)
  }

  const current   = data[activeTab]
  const pieColors = PIE_COLORS[activeTab]

  return (
    <div className="chart-root">

      {/* Header row */}
      <div className="chart-header">
        <div className="chart-header-left">
          <TrendingUpRoundedIcon style={{ fontSize: 20, color: tab.color }} />
          <div>
            <div className="chart-title">Évolution des listings</div>
            <div className="chart-sub">Données réelles depuis votre API</div>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="tab-switcher">
          {TABS.map(t => {
            const Icon = t.icon
            const isActive = t.key === activeTab
            return (
              <button
                key={t.key}
                className={`tab-btn ${isActive ? "tab-btn--active" : ""}`}
                style={isActive ? { background: t.color, borderColor: t.color } : {}}
                onClick={() => switchTab(t.key)}
              >
                <Icon style={{ fontSize: 15 }} />
                {t.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Time filters */}
      <div className="time-filters">
        <FilterAltOutlinedIcon style={{ fontSize: 15, color: "#94a3b8" }} />
        {TIME_FILTERS.map(f => (
          <button
            key={f}
            className={`time-btn ${timeFilter === f ? "time-btn--active" : ""}`}
            style={timeFilter === f ? { background: tab.color, borderColor: tab.color } : {}}
            onClick={() => setTimeFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}

        {current && (
          <div className="total-badge" style={{ color: tab.color }}>
            {current.total.toLocaleString("fr-FR")} au total
          </div>
        )}
      </div>

      {loading ? (
        <div className="chart-loading">
          <div className="chart-spinner" style={{ borderTopColor: tab.color }} />
        </div>
      ) : (
        <div className={`chart-body ${transitioning ? "chart-body--out" : ""}`}>
          <div className="chart-area-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={current?.trend || []}
                margin={{ top: 5, right: 8, left: -10, bottom: 5 }}
              >
                <defs>
                  <linearGradient id={`grad-${activeTab}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={tab.color} stopOpacity={0.18} />
                    <stop offset="95%" stopColor={tab.color} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="period"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 600 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 600 }}
                />
                <Tooltip content={<CustomTooltip color={tab.color} />} />
                <Area
                  type="monotone"
                  dataKey={activeTab}
                  stroke={tab.color}
                  strokeWidth={2.5}
                  fill={`url(#grad-${activeTab})`}
                  dot={{ fill: tab.color, strokeWidth: 0, r: 3 }}
                  activeDot={{ r: 5, fill: tab.color, stroke: "#fff", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-pie-wrap">
            <div className="pie-title">Répartition par type</div>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={current?.pie || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={72}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {(current?.pie || []).map((_, i) => (
                    <Cell key={i} fill={pieColors[i % pieColors.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-legend">
              {(current?.pie || []).map((entry, i) => (
                <div className="pie-row" key={entry.name}>
                  <span className="pie-dot" style={{ background: pieColors[i % pieColors.length] }} />
                  <span className="pie-name">{entry.name}</span>
                  <span className="pie-val" style={{ color: tab.color }}>{entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Chart